import React, { useEffect, useRef, useState } from 'react';
import { func, number, object } from 'prop-types';
import classNames from 'classnames';

import { FormattedMessage } from '../../../util/reactIntl';
import { Icons } from '../../../components';

import PriceInputs from './PriceInputs';
import { priceActiveLabel } from './filterBarHelpers';

import css from './FilterPill.module.css';

/**
 * The Price head pill (ledger "Filter system — FINAL 2026-08-25"): same pill
 * vocabulary as FilterPill, dropdown holds ONLY the min/max inputs
 * (auto-apply). Active label is the production format — "Price: Under $X",
 * "Price: $X+", "Price: $X–$Y" — never the prototype's "$0/∞" bounds.
 */
const PricePill = props => {
  const { label, urlQueryParams, getHandleChangedValueFn, totalItems } = props;
  const [isOpen, setIsOpen] = useState(false);
  const rootRef = useRef(null);
  const pillRef = useRef(null);

  const priceParam = urlQueryParams.price || null;
  const activeLabel = priceActiveLabel(priceParam);
  const isActive = !!activeLabel;

  useEffect(() => {
    if (!isOpen) return undefined;
    const onDown = e => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setIsOpen(false);
    };
    const onKey = e => {
      if (e.key === 'Escape') {
        setIsOpen(false);
        pillRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [isOpen]);

  const commit = value => getHandleChangedValueFn(true)({ price: value });

  return (
    <span className={css.root} ref={rootRef}>
      <button
        type="button"
        ref={pillRef}
        className={classNames(css.pill, {
          [css.pillActive]: isActive,
          [css.pillOpen]: isOpen,
        })}
        onClick={() => (props.onOpenOverride ? props.onOpenOverride() : setIsOpen(open => !open))}
      >
        {isActive ? `${label}: ${activeLabel}` : label}
        <span className={classNames(css.caret, { [css.caretOpen]: isOpen })}>
          <Icons name="downAngle" />
        </span>
      </button>

      {isOpen ? (
        <div className={css.dropdown}>
          <div className={css.dropdownHead}>
            <span className={css.dropdownLabel}>{label}</span>
            {isActive ? (
              <button type="button" className={css.clearButton} onClick={() => commit(null)}>
                <FormattedMessage id="FilterPill.clear" />
              </button>
            ) : null}
            <button type="button" className={css.showAction} onClick={() => setIsOpen(false)}>
              {totalItems != null ? (
                <FormattedMessage id="FilterPill.showResults" values={{ count: totalItems }} />
              ) : (
                <FormattedMessage id="FilterPill.done" />
              )}
            </button>
          </div>
          <PriceInputs value={priceParam} onCommit={commit} />
        </div>
      ) : null}
    </span>
  );
};

PricePill.defaultProps = {
  label: 'Price',
  totalItems: null,
};

PricePill.propTypes = {
  urlQueryParams: object.isRequired,
  getHandleChangedValueFn: func.isRequired,
  totalItems: number,
};

export default PricePill;
